import { useCallback, useEffect, useMemo, useState } from "react";
import type { SessionPresence } from "@waytide/core";
import { useApi } from "../api";
import { applyPresenceFrame, orderSessions } from "../model/presenceModel";

/** The live session presence: every session's status, hydrated from
 * `GET /presence` and kept current by the `presence` SSE stream (one frame
 * upserts one session by `thread_id`; a `done` session leaves the Dashboard).
 * Returns the sessions ALREADY ordered for the "who needs me" glance, plus a
 * `refresh` that re-reads `GET /presence` on demand — used right after a session
 * is assigned to a project, so the acting surface regroups from server truth
 * immediately (other surfaces catch up on the session's next presence frame).
 * Binds to the real transport (injected via `useApi`) — there is no mock layer
 * between this and the backend. */
export function usePresence(): {
  sessions: SessionPresence[];
  loaded: boolean;
  error: string | null;
  refresh: () => void;
} {
  const api = useApi();
  const [byThread, setByThread] = useState<Map<number, SessionPresence>>(
    () => new Map(),
  );
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Bumped by `refresh()` to re-run the hydration read (a `done` session that
  // already left the map won't reappear — the read returns only live rows).
  const [reloadKey, setReloadKey] = useState(0);
  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  // Hydration from GET /presence (carries the server-merged project_id +
  // machine_id sidecars). Re-runs on refresh(); the live SSE frames below keep
  // it current between reads.
  useEffect(() => {
    let alive = true;
    api
      .getPresence()
      .then((res) => {
        if (!alive) return;
        // The read is authoritative: it returns exactly the live rows (a `done`
        // session is gone), so replace the map rather than merge — no stale row
        // can linger across a refresh.
        setByThread(new Map(res.sessions.map((s) => [s.thread_id, s])));
        setLoaded(true);
      })
      .catch((e) => {
        if (!alive) return;
        setError(e instanceof Error ? e.message : String(e));
        setLoaded(true);
      });
    return () => {
      alive = false;
    };
  }, [api, reloadKey]);

  // Live updates over the presence SSE: upsert by thread_id (preserving the
  // last known project_id / machine_id when a frame omits them); a `done`
  // session leaves the map (it is no longer something to watch).
  useEffect(() => {
    return api.subscribeChat(
      () => {},
      () => {},
      (p) => setByThread((prev) => applyPresenceFrame(prev, p)),
    );
  }, [api]);

  const sessions = useMemo(
    () => orderSessions([...byThread.values()]),
    [byThread],
  );
  return { sessions, loaded, error, refresh };
}
